import {DataFetcher} from '@aboutyou/data-fetcher';
import {DeliverySection as DeliverySectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {
  BasketIconName,
  BasketLoginButton,
  BasketSection,
  EmptyState,
  FONT_SIZES,
  FONT_WEIGHTS,
  Icon,
  Text,
  tabletAndUp,
  useIsomorphicLayoutEffect,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import {updateBasketSplitView} from '../BasketSplitView/BasketSplitView';
import {useDeliveriesSection} from '../hooks/useGetBasketPageSection';

import {useTranslation} from '@aboutyou/i18n';
import {isCaseTypeCheck} from '@aboutyou/utils';
import styled, {css} from 'styled-components';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';

export const EmptySection: FC<{
  className?: string;
  resizeEnabled?: boolean;
}> = props => {
  return (
    <DataFetcher
      request={useDeliveriesSection()}
      View={EmptySectionView}
      Error={() => null}
      Loading={() => null}
      viewProps={props}
    />
  );
};

export const EmptySectionView: FC<
  DeliverySectionType & {
    className?: string;
    resizeEnabled?: boolean;
  }
> = ({className, type, resizeEnabled = false}) => {
  const {isAuthenticated} = useIsAuthenticated();
  const {t} = useTranslation();

  const isEmpty = isCaseTypeCheck('empty', type);
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  useIsomorphicLayoutEffect(() => {
    if (resizeEnabled) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      updateBasketSplitView({isSplit: !isEmpty});
    }
  }, [isEmpty, resizeEnabled]);

  if (!isEmpty) {
    return null;
  }

  const onLoginClick = () => {
    showUserAuthenticationPrompt({
      needsCheckoutSession: false,
    });
  };

  return (
    <BasketSection>
      <div data-testid="emptyBasketContainer" className={className}>
        <EmptyState
          data-testid={'emptyBasketMessageContainer'}
          title={
            <StyledHeading as="h1" data-testid={'emptyBasketMessage'}>
              {t('Your basket is empty!')}
            </StyledHeading>
          }
          subtitle={t('Add products or login')}
          asset={
            <StyledIcon
              data-testid="emptyBasketIcon"
              asset={BasketIconName.Empty}
            />
          }
          cta={
            !isAuthenticated && (
              <BasketLoginButton
                onClick={onLoginClick}
                dataTestId={'emptyBasketLoginButton'}
              />
            )
          }
        />
      </div>
    </BasketSection>
  );
};

const StyledIcon = styled(Icon)`
  width: 175px;
  height: 100px;
  ${tabletAndUp(css`
    width: 300px;
    height: 224px;
  `)}
`;

const StyledHeading = styled(Text)`
  font-weight: ${FONT_WEIGHTS.bold};
  font-size: ${FONT_SIZES.px20};

  ${tabletAndUp(css`
    font-size: ${FONT_SIZES.px24};
  `)}
`;
